import { useState } from 'react';
import { Check, ChevronDown } from 'lucide-react';
import type { ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandGroup,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';

interface FilterComboboxProps {
  /** Text shown on the trigger and as the first item when nothing is picked. */
  allLabel: string;
  /** Stable accessible name when the visible value changes. */
  ariaLabel?: string;
  /** `'All'` or one of `options`. */
  value: string;
  options: Array<string>;
  onChange: (value: string) => void;
  /** Optional display for an option value (e.g. ISO-2 country code → name). */
  renderOption?: (value: string) => ReactNode;
}

// Ghost "All X ▾" combobox filter used across the Starting-Universe dropdowns
// (index picker + single-security exclusion search). Extracted from
// UniverseSelector so both dropdowns share ONE filter control — do not fork.
export function FilterCombobox({
  allLabel,
  ariaLabel,
  value,
  options,
  onChange,
  renderOption,
}: FilterComboboxProps) {
  const [open, setOpen] = useState(false);
  const label = (v: string) => (renderOption ? renderOption(v) : v);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          role="combobox"
          aria-label={ariaLabel}
          aria-expanded={open}
          className="h-7 text-[13px] border-0 bg-transparent shadow-none p-0 gap-1.5 w-auto justify-start text-muted-foreground hover:text-foreground hover:bg-transparent"
        >
          {value === 'All' ? allLabel : label(value)}
          <ChevronDown className="ml-1 h-3 w-3 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[220px] p-0" align="start">
        <Command>
          <CommandList className="max-h-[300px]">
            <CommandGroup>
              <CommandItem
                onSelect={() => {
                  onChange('All');
                  setOpen(false);
                }}
              >
                <Check
                  className={cn(
                    'mr-2 h-4 w-4',
                    value === 'All' ? 'opacity-100' : 'opacity-0',
                  )}
                />
                {allLabel}
              </CommandItem>
              {options.map((option) => (
                <CommandItem
                  key={option}
                  value={option}
                  onSelect={() => {
                    onChange(option);
                    setOpen(false);
                  }}
                >
                  <Check
                    className={cn(
                      'mr-2 h-4 w-4',
                      value === option ? 'opacity-100' : 'opacity-0',
                    )}
                  />
                  {label(option)}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
